import NewReleasesIcon from '@mui/icons-material/NewReleases';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Tooltip } from '@mui/material';
import UTILS from '@siloreal:modules:private/silobag-list/config/extra/utils';
import * as HOOKS from '@siloreal:shared:hooks';

const VRI_STATUS = {
  approved: {
    accentColor: '#68BD00',
    backgroundColor: '#D4F5C8',
    icon: VerifiedIcon,
    tooltipKey: 'temp.private.list.silospro.vri_approved',
  },
  rejected: {
    accentColor: '#FF1414',
    backgroundColor: '#FFD4D8',
    icon: NewReleasesIcon,
    tooltipKey: 'temp.private.list.silospro.vri_rejected',
  },
  pending: {
    accentColor: '#D6A600',
    backgroundColor: '#FFF9DB',
    icon: PendingOutlinedIcon,
    tooltipKey: 'temp.private.list.silospro.vri_pending',
  },
} as const;

type VriTag = { label: string };
type VriStatus = keyof typeof VRI_STATUS;
type Translate = (scope: string) => string;

function getVriStatus(tag: VriTag): VriStatus {
  if (UTILS.is.vri.approved(tag)) return 'approved';
  if (UTILS.is.vri.rejected(tag)) return 'rejected';
  return 'pending';
}

function formatVriBadgeLabel(status: VriStatus, level: number, translate: Translate): string {
  return `${translate(VRI_STATUS[status].tooltipKey)} · ${translate(
    'temp.private.list.silospro.vri_level'
  )} ${level}`;
}

export function getVriBadgeLabel(tag: VriTag, translate: Translate): string | null {
  const level = UTILS.extract.level.vri(tag);
  return level ? formatVriBadgeLabel(getVriStatus(tag), level, translate) : null;
}

export function VriBadge({ tag }: { tag: VriTag }) {
  const i18n = HOOKS.useLocale();
  const level = UTILS.extract.level.vri(tag);
  if (!level) return null;

  const status = getVriStatus(tag);
  const { accentColor, backgroundColor, icon: Icon } = VRI_STATUS[status];
  const tooltip = formatVriBadgeLabel(status, level, i18n.t);

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      <Box
        component="span"
        aria-label={tooltip}
        sx={{
          position: 'relative',
          width: 64,
          height: 26,
          flexShrink: 0,
          overflow: 'hidden',
          borderRadius: '8px',
          border: '1px solid',
          borderColor: `${accentColor}33`,
          backgroundColor,
          cursor: 'default',
        }}
      >
        <Icon
          aria-hidden
          sx={{
            position: 'absolute',
            top: '50%',
            left: 5,
            transform: 'translateY(-50%)',
            color: accentColor,
            fontSize: 16,
          }}
        />
        <Box
          component="span"
          aria-hidden
          sx={{
            position: 'absolute',
            inset: '0 0 0 auto',
            width: 44,
            pl: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${accentColor}24`,
            clipPath: 'polygon(28% 0, 100% 0, 100% 100%, 0 100%)',
            color: accentColor,
            fontSize: 11,
            fontWeight: 800,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'center',
          }}
        >
          Lv. {level}
        </Box>
      </Box>
    </Tooltip>
  );
}
